<template>
  <article class="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group border border-slate-200">
    <router-link :to="`../article/${article.slug}`" class="block text-inherit no-underline">
      <!-- Image Container -->
      <div class="relative w-full h-48 overflow-hidden bg-slate-100">
        <img
          :src="article.imageBase64"
          :alt="article.title"
          loading="lazy"
          class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div
          class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        ></div>

        <!-- Article Status Badges -->
        <div class="absolute top-3 left-3 flex gap-2">
          <span v-if="article.isFeatured" class="px-2 py-1 bg-yellow-500 text-white text-xs font-medium rounded-full">
            Featured
          </span>
          <span v-if="article.isTrending" class="px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full">
            Trending
          </span>
        </div>

        <!-- Reading Time -->
        <div class="absolute top-3 right-3">
          <span class="px-2 py-1 bg-black/70 text-white text-xs rounded-full backdrop-blur-sm"> {{ article.readingTimeMinutes || 3 }}min read </span>
        </div>
      </div>

      <!-- Content -->
      <div class="p-6">
        <!-- Section & Date -->
        <div class="flex items-center justify-between text-sm text-slate-500 mb-3">
          <span class="font-medium text-blue-600">{{ article.section }}</span>
          <time :datetime="article.date" class="text-slate-400">
            {{ formatDate(article.date) }}
          </time>
        </div>

        <!-- Title -->
        <h3 class="text-lg font-bold text-slate-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors duration-200 leading-tight">
          {{ article.title }}
        </h3>

        <!-- Description -->
        <p class="text-slate-600 text-sm line-clamp-3 leading-relaxed mb-4">
          {{ article.description }}
        </p>

        <!-- Tags -->
        <div v-if="article.tags && article.tags.length" class="flex flex-wrap gap-2 mb-4">
          <span
            v-for="tag in article.tags.slice(0, 3)"
            :key="tag"
            class="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full hover:bg-blue-100 hover:text-blue-700 transition-colors cursor-pointer"
            @click.prevent="$emit('tagClick', tag)"
          >
            {{ tag }}
          </span>
          <span v-if="article.tags.length > 3" class="px-2 py-1 text-slate-500 text-xs"> +{{ article.tags.length - 3 }} more </span>
        </div>

        <!-- Author & Metrics - Disabled for now.-->
        <!-- <div class="flex items-center justify-between pt-4 border-t border-slate-100">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
              {{ getAuthorInitials(article.author) }}
            </div>
            <div>
              <p class="text-sm font-medium text-slate-900">{{ article.author || 'HardwarePulse' }}</p>
            </div>
          </div>

          Engagement Metrics
          <div class="flex items-center gap-4 text-slate-400">
            <div v-if="article.metrics?.viewCount" class="flex items-center gap-1 text-xs">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                ></path>
              </svg>
              {{ formatNumber(article.metrics.viewCount) }}
            </div>

            <div class="flex items-center gap-1 text-xs">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </div>
          </div>
        </div> -->
      </div>
    </router-link>
  </article>
</template>

<script setup>
  import { defineEmits } from 'vue';

  // Props
  const props = defineProps({
    article: {
      type: Object,
      required: true,
    },
  });

  // Emits
  const emit = defineEmits(['tagClick']);

  // Helper functions
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    }
  };

  const getAuthorInitials = (authorName) => {
    if (!authorName) return 'HP';
    return authorName
      .split(' ')
      .map((name) => name.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };
</script>

<style scoped>
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
</style>
